import { useCallback, useMemo, useState } from 'react';
import { ArrowClockwise, Info, Plus, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import { useActiveDeposits, useActiveLocations, useActiveLpnPrefixes } from '../lpnPickers';
import BalancePreview from './BalancePreview';
import DestinationEditor from './DestinationEditor';
import FifoNotice from './FifoNotice';
import PickDestinationEditor from './PickDestinationEditor';
import RepackReceipt from './RepackReceipt';
import SourceLpnPanel, { LoadSourceLpn } from './SourceLpnPanel';
import { MergeLpns, PickFractional, PreviewRepack, SplitLpn } from './repackApi';
import {
    BuildDraftLines,
    BuildWireLines,
    CreateDestinationDraft,
    CreateSourceDraft,
    DESTINATION_NEW,
    IsLpnCreationForbiddenError,
    IsPickWithoutDestinationError,
    ParseRepackBalance,
    ParseRepackEffects,
    ParseRepackWarnings,
    PICK_DESTINATION_PRODUCTION_ORDER,
    REPACK_MERGE,
    REPACK_OPERATIONS,
    REPACK_PICK,
    REPACK_SPLIT,
} from './repackModel';

/**
 * CONSOLE DO REPACK (M7) — Supervisor, desktop.
 *
 * ── A disciplina que manda nesta tela ───────────────────────────────────────
 * A do preview de alocação do M5: **o que a tela promete é o que o banco vai
 * gravar**. Aqui isso é literal — a conferência chama `POST /repack/preview`
 * (`dry_run` no serviço), que roda a MESMA função da gravação e devolve o razão
 * pronto. Nenhum saldo é somado no navegador.
 *
 * A consequência prática está em `mutate`: **qualquer edição descarta a
 * prévia**. Uma prévia velha ao lado de um formulário novo é pior do que
 * nenhuma — ela promete uma conta que ninguém vai executar. Por isso o botão de
 * confirmar só existe depois de uma conferência FRESCA.
 *
 * ── As três operações, e a que mais engana ──────────────────────────────────
 * SPLIT (1 → N) e MERGE (N → 1) movem saldo entre pallets. O PICKING
 * FRACIONADO **não gera pallet**: ele credita num consumo de OP ou num volume
 * de expedição, e sem um dos dois o servidor recusa — porque sem destino o
 * saldo sairia do sistema (invariante do §3). A tela diz isso ANTES de tentar.
 *
 * ── FIFO ────────────────────────────────────────────────────────────────────
 * Aparece como aviso ao lado do botão e **nunca** desabilita nada (§6.2 nega a
 * trava explicitamente). Ver `FifoNotice`.
 */

const EMPTY_PICK_DESTINATION = {
    kind: PICK_DESTINATION_PRODUCTION_ORDER,
    productionOrderId: '',
    outboundId: '',
    volumeCode: '',
};

const RepackWorkspace = ({ label, canCreateLpn = false }) => {
    const [operation, setOperation] = useState(REPACK_SPLIT);
    const [sources, setSources] = useState(() => [CreateSourceDraft()]);
    const [destinations, setDestinations] = useState(() => [CreateDestinationDraft()]);
    const [pickDestination, setPickDestination] = useState(EMPTY_PICK_DESTINATION);

    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [result, setResult] = useState(null);

    const prefixes = useActiveLpnPrefixes(canCreateLpn);
    const deposits = useActiveDeposits(canCreateLpn);
    const locations = useActiveLocations(canCreateLpn);

    const isSplit = operation === REPACK_SPLIT;
    const isMerge = operation === REPACK_MERGE;
    const isPick = operation === REPACK_PICK;

    /**
     * Toda mutação de formulário passa por aqui — e toda mutação MATA a prévia.
     * Sem isso, a tela mostraria o saldo de uma operação e gravaria outra.
     */
    const mutate = useCallback((apply) => {
        setPreview(null);
        setPreviewError(null);
        setSubmitError(null);
        apply();
    }, []);

    const resetAll = (nextOperation = operation) => {
        setOperation(nextOperation);
        setSources([CreateSourceDraft()]);
        setDestinations([CreateDestinationDraft()]);
        setPickDestination(EMPTY_PICK_DESTINATION);
        setPreview(null);
        setPreviewError(null);
        setSubmitError(null);
        setResult(null);
    };

    const patchSource = (key, patch) => {
        mutate(() => {
            setSources((previous) => previous.map((row) => (row.key === key ? { ...row, ...patch } : row)));
            // No split as quantidades vivem em CADA destino: quando o pallet de
            // origem entra, os destinos ganham as linhas dele. Trocar o pallet
            // sem trocar as linhas deixaria produtos de outro pallet no
            // formulário — o jeito silencioso de pedir baixa do saldo errado.
            if (patch.lpn !== undefined) {
                const lines = patch.lpn ? BuildDraftLines(patch.lpn.items || []) : [];
                setDestinations((previous) => previous.map((row) => ({ ...row, lines: lines.map((l) => ({ ...l })) })));
            }
        });
    };

    const patchDestination = (key, patch) => {
        mutate(() => {
            setDestinations((previous) => previous.map((row) => (row.key === key ? { ...row, ...patch } : row)));
        });
    };

    const source = sources[0];
    const target = destinations[0];

    // ── O corpo do wire (o MESMO da prévia e da gravação) ───────────────────
    const buildBody = useCallback(() => {
        if (isSplit) {
            const wireDestinations = destinations
                .map((row) => {
                    const items = BuildWireLines(row.lines);
                    if (items.length === 0) {
                        return null;
                    }
                    return row.mode === DESTINATION_NEW
                        ? {
                              new_lpn: {
                                  prefix_code: row.prefixCode,
                                  location_id: row.locationId,
                                  deposit_id: row.depositId,
                                  is_bulky: row.isBulky,
                              },
                              items,
                          }
                        : { lpn_code: row.code.trim(), items };
                })
                .filter(Boolean);
            return { source_lpn_code: source?.lpn?.code || source?.code || '', destinations: wireDestinations };
        }

        if (isMerge) {
            const wireSources = sources
                .map((row) => {
                    const items = BuildWireLines(row.lines);
                    if (items.length === 0) {
                        return null;
                    }
                    return { lpn_code: row.lpn?.code || row.code.trim(), items };
                })
                .filter(Boolean);
            const body = { sources: wireSources };
            if (target?.mode === DESTINATION_NEW) {
                body.new_lpn = {
                    prefix_code: target.prefixCode,
                    location_id: target.locationId,
                    deposit_id: target.depositId,
                    is_bulky: target.isBulky,
                };
            } else {
                body.target_lpn_code = target?.code.trim() || '';
            }
            return body;
        }

        const items = BuildWireLines(source?.lines || []);
        const body = { source_lpn_code: source?.lpn?.code || source?.code || '', items };
        if (pickDestination.kind === PICK_DESTINATION_PRODUCTION_ORDER) {
            if (pickDestination.productionOrderId) {
                body.destination = { production_order_id: pickDestination.productionOrderId };
            }
        } else if (pickDestination.outboundId && pickDestination.volumeCode.trim()) {
            body.destination = {
                shipment: {
                    outbound_id: pickDestination.outboundId,
                    volume_code: pickDestination.volumeCode.trim(),
                },
            };
        }
        return body;
    }, [destinations, isMerge, isSplit, pickDestination, source, sources, target]);

    /**
     * O que ainda falta para a operação existir. É a MESMA lista que desabilita
     * o botão e que aparece escrita — "botão cinza sem explicação" é o jeito
     * mais rápido de fazer alguém achar que o sistema travou.
     */
    const missing = useMemo(() => {
        const reasons = [];
        if (!source?.lpn) {
            reasons.push(label('wms.repack.source_hint'));
            return reasons;
        }
        if (isSplit) {
            const usable = destinations.filter((row) => BuildWireLines(row.lines).length > 0);
            if (usable.length === 0) {
                reasons.push(label('wms.repack.nothing_to_move'));
            }
            for (const row of usable) {
                if (row.mode === DESTINATION_NEW) {
                    if (!row.prefixCode || !row.locationId || !row.depositId) {
                        reasons.push(label('wms.repack.destination_needs_draft'));
                    }
                } else if (!row.code.trim()) {
                    reasons.push(label('wms.repack.destination_needs_code'));
                }
            }
        }
        if (isMerge) {
            const usable = sources.filter((row) => row.lpn && BuildWireLines(row.lines).length > 0);
            if (usable.length === 0) {
                reasons.push(label('wms.repack.nothing_to_move'));
            }
            if (target?.mode === DESTINATION_NEW) {
                if (!target.prefixCode || !target.locationId || !target.depositId) {
                    reasons.push(label('wms.repack.destination_needs_draft'));
                }
            } else if (!target?.code.trim()) {
                reasons.push(label('wms.repack.destination_needs_code'));
            }
        }
        if (isPick) {
            const lines = BuildWireLines(source.lines);
            if (lines.length === 0) {
                reasons.push(label('wms.repack.nothing_to_move'));
            }
            // O fracionado para OP é UM lote por chamada — é o gesto da bancada
            // do M6, e o backend recusa mais de um. A tela diz isso antes.
            if (lines.length > 1 && pickDestination.kind === PICK_DESTINATION_PRODUCTION_ORDER) {
                reasons.push(label('wms.repack.pick_op_single_line'));
            }
            const hasDestination =
                pickDestination.kind === PICK_DESTINATION_PRODUCTION_ORDER
                    ? Boolean(pickDestination.productionOrderId)
                    : Boolean(pickDestination.outboundId && pickDestination.volumeCode.trim());
            if (!hasDestination) {
                // NÃO é falha: é a escolha que ainda não foi feita (§3).
                reasons.push(label('wms.repack.pick_destination_missing'));
            }
        }
        return [...new Set(reasons)];
    }, [destinations, isMerge, isPick, isSplit, label, pickDestination, source, sources, target]);

    const runPreview = async () => {
        setPreviewing(true);
        setPreviewError(null);
        setSubmitError(null);
        try {
            const payload = await PreviewRepack({ operation, ...buildBody() });
            setPreview({
                balance: ParseRepackBalance(payload),
                warnings: ParseRepackWarnings(payload),
                effects: ParseRepackEffects(payload),
                raw: payload,
            });
        } catch (error) {
            setPreview(null);
            setPreviewError(error);
        } finally {
            setPreviewing(false);
        }
    };

    const commit = async () => {
        setSaving(true);
        setSubmitError(null);
        try {
            const body = buildBody();
            const payload = isSplit
                ? await SplitLpn(body)
                : isMerge
                  ? await MergeLpns(body)
                  : await PickFractional(body);
            setResult(payload);
        } catch (error) {
            setSubmitError(error);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Erro de negócio traduzido para a INSTRUÇÃO que ele é. Dois casos ganham
     * texto próprio porque não são falhas do operador:
     *   · 409 sem destino no fracionado → falta escolher para onde vai;
     *   · 403 de criação de LPN → §6.2, "peça a etiqueta ao Supervisor".
     */
    const renderError = (error) => {
        if (!error) {
            return null;
        }
        const isInstruction = IsPickWithoutDestinationError(error) || IsLpnCreationForbiddenError(error);
        const text = IsPickWithoutDestinationError(error)
            ? label('wms.repack.pick_destination_missing')
            : IsLpnCreationForbiddenError(error)
              ? `${label('wms.repack.new_lpn_forbidden_title')}. ${label('wms.repack.new_lpn_forbidden_body')}`
              : error?.message || label('wms.common.load_error');

        return (
            <p
                role="alert"
                className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                    isInstruction
                        ? 'border-info-400 bg-info-soft text-info-text dark:border-info-700 dark:bg-info-900/25 dark:text-info-200'
                        : 'border-danger-400 bg-danger-soft text-danger-text dark:border-danger-700 dark:bg-danger-900/25 dark:text-danger-200'
                }`}
            >
                {isInstruction ? (
                    <Info size={16} className="mt-0.5 shrink-0" />
                ) : (
                    <WarningCircle size={16} className="mt-0.5 shrink-0" />
                )}
                <span>{text}</span>
            </p>
        );
    };

    if (result) {
        return <RepackReceipt label={label} operation={operation} result={result} onRestart={() => resetAll()} />;
    }

    return (
        <div className="space-y-5">
            {/* Escolha da operação. As três são o mesmo módulo (o saldo muda de
                lugar); o que muda é a cardinalidade e a trilha gravada. */}
            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {REPACK_OPERATIONS.map((key) => {
                        const isActive = key === operation;
                        return (
                            <button
                                type="button"
                                key={key}
                                onClick={() => resetAll(key)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {label(`wms.repack.tab.${key}`)}
                            </button>
                        );
                    })}
                </nav>
            </div>

            <p className="text-sm text-mono-600 dark:text-mono-400">{label(`wms.repack.tab_hint.${operation}`)}</p>

            {/* ── ORIGEM ─────────────────────────────────────────────────── */}
            <section className="space-y-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {isMerge ? label('wms.repack.sources_title') : label('wms.repack.source_title')}
                </h2>
                {sources.map((row, index) => (
                    <SourceLpnPanel
                        key={row.key}
                        label={label}
                        source={row}
                        onChange={(patch) => patchSource(row.key, patch)}
                        onRemove={
                            isMerge && sources.length > 1
                                ? () =>
                                      mutate(() =>
                                          setSources((previous) => previous.filter((entry) => entry.key !== row.key)),
                                      )
                                : null
                        }
                        // No split as quantidades ficam nos DESTINOS (cada um diz
                        // quanto leva); aqui o pallet é só a origem.
                        showLines={!isSplit}
                        autoFocus={index === 0}
                        disabled={saving}
                    />
                ))}
                {isMerge ? (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => mutate(() => setSources((previous) => [...previous, CreateSourceDraft()]))}
                        disabled={saving}
                    >
                        <Plus size={16} className="mr-2" />
                        {label('wms.repack.add_source')}
                    </Button>
                ) : null}
            </section>

            {/* ── DESTINO ────────────────────────────────────────────────── */}
            {isPick ? (
                <PickDestinationEditor
                    label={label}
                    destination={pickDestination}
                    onChange={(patch) => mutate(() => setPickDestination((previous) => ({ ...previous, ...patch })))}
                    disabled={saving}
                />
            ) : (
                <section className="space-y-3">
                    <h2 className="text-sm font-bold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {isMerge ? label('wms.repack.merge_target_title') : label('wms.repack.destinations_title')}
                    </h2>
                    {(isMerge ? [target] : destinations).map((row, index) => (
                        <DestinationEditor
                            key={row.key}
                            label={label}
                            destination={row}
                            onChange={(patch) => patchDestination(row.key, patch)}
                            onRemove={
                                isSplit && destinations.length > 1
                                    ? () =>
                                          mutate(() =>
                                              setDestinations((previous) =>
                                                  previous.filter((entry) => entry.key !== row.key),
                                              ),
                                          )
                                    : null
                            }
                            canCreateLpn={canCreateLpn}
                            prefixes={prefixes}
                            deposits={deposits}
                            locations={locations}
                            showLines={isSplit}
                            title={isSplit ? `${label('wms.repack.destination')} ${index + 1}` : null}
                            disabled={saving}
                        />
                    ))}
                    {isSplit ? (
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() =>
                                mutate(() =>
                                    setDestinations((previous) => [
                                        ...previous,
                                        { ...CreateDestinationDraft(), lines: BuildDraftLines(source?.lpn?.items || []) },
                                    ]),
                                )
                            }
                            disabled={saving || !source?.lpn}
                        >
                            <Plus size={16} className="mr-2" />
                            {label('wms.repack.add_destination')}
                        </Button>
                    ) : null}
                </section>
            )}

            {/* ── CONFERÊNCIA + CONFIRMAÇÃO ──────────────────────────────── */}
            <section className="space-y-3 rounded-xl border-2 border-mono-300 p-4 dark:border-mono-700">
                {missing.length > 0 ? (
                    <ul className="space-y-1">
                        {missing.map((reason) => (
                            <li
                                key={reason}
                                className="flex items-start gap-2 text-sm text-mono-600 dark:text-mono-400"
                            >
                                <Info size={15} className="mt-0.5 shrink-0" />
                                {reason}
                            </li>
                        ))}
                    </ul>
                ) : null}

                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        type="button"
                        variant={preview ? 'secondary' : 'primary'}
                        onClick={runPreview}
                        loading={previewing}
                        disabled={missing.length > 0 || saving}
                    >
                        <ArrowClockwise size={16} className="mr-2" />
                        {preview ? label('wms.repack.preview_again') : label('wms.repack.preview')}
                    </Button>
                    {preview ? null : (
                        <span className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.repack.preview_required')}
                        </span>
                    )}
                    <span className="ml-auto text-xs text-mono-400 dark:text-mono-500">
                        {label('wms.repack.console_typed_note')}
                    </span>
                </div>

                {renderError(previewError)}
                {renderError(submitError)}

                {preview ? (
                    <div className="space-y-3">
                        {/* O aviso de FIFO vem ANTES do botão de confirmar, e o
                            botão continua habilitado: o objetivo é que ele seja
                            LIDO, não que ele impeça. Pôr o confirmar em cima
                            transformaria o aviso em rodapé decorativo. */}
                        <FifoNotice
                            warnings={preview.warnings}
                            chosenCode={source?.lpn?.code || null}
                            label={label}
                            onUseLpn={
                                isMerge
                                    ? null
                                    : (code) =>
                                          LoadSourceLpn({
                                              code,
                                              label,
                                              onChange: (patch) => patchSource(source.key, patch),
                                          })
                            }
                        />
                        <BalancePreview balance={preview.balance} effects={preview.effects} label={label} />
                        <div className="flex flex-wrap items-center gap-3 border-t border-mono-200 pt-3 dark:border-mono-800">
                            <Button
                                type="button"
                                onClick={commit}
                                loading={saving}
                                disabled={!preview.balance?.balanced}
                            >
                                {label(`wms.repack.confirm.${operation}`)}
                            </Button>
                            <span className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.repack.preview_stale')}
                            </span>
                        </div>
                    </div>
                ) : null}
            </section>
        </div>
    );
};

export default RepackWorkspace;
